"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="84972" className="hidden lg:flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-9 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex" viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-semibold text-secondary transition-colors hover:text-primary focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 relative after:absolute after:bottom-1 after:left-4 after:right-4 after:h-[2px] after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-6 px-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-12 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-2 mt-6 px-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onNavigate} className="flex items-center py-4 text-lg font-semibold text-secondary border-b border-border/50 hover:text-primary transition-colors">
          {item.label}
        </Link>)}
    </div>;
}
export default function ClinicHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-md" : "bg-background border-b border-transparent"}`}>
      <MenuProvider menu_id="41" className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
              <img src="https://dentistforsleepapnea.com/wp-content/uploads/2026/04/523944_1670889061_Color-scaled.png" alt="Dentist for Sleep Apnea" className="h-12 md:h-10 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="flex-1 flex justify-center">
            <DesktopMenuConsumer />
          </div>

          {}
          <div className="flex items-center gap-3 sm:gap-4">
            <Button asChild variant="default" className="rounded-full bg-primary text-primary-foreground shadow-md hover:shadow-lg transition-all duration-300">
              <Link to="tel:+13108587373">
                <Phone className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline font-bold tracking-wider">(310) 858-7373</span>
              </Link>
            </Button>

            {}
            <div className="lg:hidden flex items-center">
              <SafeHydrate><Drawer open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen} direction="right">
                <DrawerTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full hover:bg-accent">
                    <Menu className="h-6 w-6 text-secondary" />
                    <span className="sr-only">Open main menu</span>
                  </Button>
                </DrawerTrigger>
                <DrawerContent className="h-full w-[85vw] sm:w-[350px] rounded-none border-l border-border bg-background">
                  <DrawerHeader className="flex items-center justify-between border-b border-border/50 pb-4 pt-6 px-4">
                    <DrawerTitle className="text-left">
                      <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1621270/img/dentist_sleep_apnea_logo.png" alt="Dentist for Sleep Apnea" className="h-7 w-auto object-contain" />
                    </DrawerTitle>
                    <DrawerClose asChild>
                      <Button variant="ghost" size="icon" className="rounded-full">
                        <X className="h-5 w-5 text-secondary" />
                        <span className="sr-only">Close menu</span>
                      </Button>
                    </DrawerClose>
                  </DrawerHeader>
                  <div className="flex-1 overflow-y-auto">
                    <MobileMenuConsumer onNavigate={() => setIsMobileMenuOpen(false)} />
                  </div>
                  <div className="p-6 border-t border-border/50 bg-muted/30">
                    <p className="text-sm text-muted-foreground mb-3 font-semibold uppercase tracking-widest">Immediate Assistance</p>
                    <Button asChild className="w-full rounded-full bg-primary text-primary-foreground" size="lg">
                      <Link to="tel:+13108587373">
                        <Phone className="w-4 h-4 mr-2" />
                        Call (310) 858-7373
                      </Link>
                    </Button>
                  </div>
                </DrawerContent>
              </Drawer></SafeHydrate>
            </div>
          </div>
        </div>
      </MenuProvider>
    </header>;
}